import {
	Box,
	Button,
	Table,
	Text,
	Group,
	useMantineTheme,
	Skeleton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { SessionContext } from "@/context/SessionContext";
import appSettings from "../appsettings.json";
import { Utils } from "../javascript/utils";
import {
	IconDownload,
	IconMathGreater,
	IconCameraCheck,
	IconCameraSearch,
	IconCameraExclamation,
	IconCameraRotate,
	IconSalad,
	IconCameraDown,
} from "@tabler/icons-react";
import React, { useEffect, useState, useContext } from "react";
import { ProjectSubmission } from "@/javascript/project-objects";
import ConductionAPI from "@/javascript/conduction-api";

const EditorTasks: React.FC = () => {
	const [mySubmissions, setMySubmissions] = useState<ProjectSubmission[]>([]);
	const [expandedSubmissions, setExpandedSubmissions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const theme = useMantineTheme();

	// check isMobile once, set text sizes accordingly
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;
	const fzMed = isMobile ? 14 : 18;

	const HEADER_FW = 700;
	const TASKS_HEIGHT = isMobile ? 530 : 440;

	const [isUploading, setIsUploading] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) {
		return null;
	}

	async function fetchBlob(url: string): Promise<Blob> {
		const res = await fetch(url, { cache: "force-cache" });
		if (!res.ok) throw new Error("Fetch failed");
		const blob = await res.blob();
		return blob;
	}

	function downloadBlob(url: string, filename: string) {
		fetchBlob(url)
			.then((blob) => {
				const blobUrl = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = blobUrl;
				link.download = filename;
				link.click();
				URL.revokeObjectURL(blobUrl);
			})
			.catch((error) => {
				console.error("Download error:", error);
			});

		return () => {};
	}

	const uploadFile = async (file: File, submissionID: string) => {
		const token = session.token;
		if (!token) return;
		try {
			setIsUploading(true);
			await ConductionAPI.fulfillProjectSubmission(submissionID, file, token);
			Utils.displayNotification(
				"Video Submitted Successfully",
				"video file uploaded",
				"green",
				<IconCameraCheck size={fzMed} />
			);
			session.fetchProjectSubmissions();
		} catch (error) {
			Utils.displayNotification(
				"Error: Video Not Submitted",
				"",
				"red",
				<IconSalad size={fzMed} />
			);
		} finally {
			setIsUploading(false);
		}
	};

	const createFilePicker = (submissionID: string) => {
		if (isUploading) return;
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "video/*";
		input.onchange = (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				uploadFile(file, submissionID);
			}
		};
		input.click();
	};

	// Column widths for consistent spacing
	// sum(parent or child row %) = 100% - (due date %)
	const CW = {
		desktop: {
			parent: {
				dueDate: "23%",
				studentName: "23%",
				projectName: "23%",
				uploadVideo: "23%",
				expand: "8%",
			},
			child: {
				indent: "23%",
				taskFulfilledDate: "23%",
				instrumentName: "23%",
				downloadClip: "23%",
				rightIndent: "8%",
			},
		},
		mobile: {
			parent: {
				dueDate: "23%",
				names: "37%",
				uploadVideo: "30%",
				expand: "10%",
			},
			child: {
				indent: "0%",
				taskFulfilledDate: "33%",
				instrumentName: "33%",
				downloadClip: "33%",
				rightIndent: "0%",
			},
		},
	};

	// for mobile
	function getTruncatedString(s: string) {
		return s.length > 5 ? s.slice(0, 10) + "..." : s;
	}

	// multiple rows can be expanded at a time, all rows begin in the expanded state
	const toggleExpand = (submissionID: string) => {
		setExpandedSubmissions((prev) =>
			prev.includes(submissionID)
				? prev.filter((id) => id !== submissionID)
				: [...prev, submissionID]
		);
	};

	// Create skeleton loading rows
	const skeletonCell = <Skeleton height={20} ta="center" />;
	const skeletonRow = (
		<Table.Tr bg={theme.colors.blue[1]}>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			{!isMobile && <Table.Td fz={fzSmall} w={"25%"} ta="center">
							{skeletonCell}
						</Table.Td>}
		</Table.Tr>
	);

	const skeletonRows = Array.from({ length: 4 }, (_, i) =>
		React.cloneElement(skeletonRow, { key: `s${i}` })
	);

	useEffect(() => {
		setIsLoading(true);
		if (!session.projectSubmissions) {
			return;
		}
		const today = new Date();
		setMySubmissions(
			// if submission hasn't yet been fulfilled, or
			// if editor due date has not yet passed
			session.projectSubmissions
				? session.projectSubmissions.filter(
						(submission) =>
							(submission.videoEditor?.id === session.user?.id ||
								submission.videoEditor?.id == null) &&
							(submission.dateFulfilled == null || submission.editorDueDate > today) &&
							submission.instrumenatlistTasks.length > 0
				  )
				: []
		);
		setExpandedSubmissions(
			session.projectSubmissions?.map((submission) => submission.id) ?? []
		);
		setIsLoading(false);
	}, [session.projectSubmissions]);

	const rows = mySubmissions?.map((parentRow) => {
		const isExpanded = expandedSubmissions.includes(parentRow.id);
		return (
			<React.Fragment key={parentRow.id}>
				<Table.Tr
					onClick={() => toggleExpand(parentRow.id)}
					bg={theme.colors.blue[1]}
					style={{
						cursor: "pointer",
					}}
				>
					{/* Due Date */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
						ta="center"
					>
						{new Date(parentRow.editorDueDate).toLocaleString()}
					</Table.Td>
					{isMobile ? (
						<>
							<Table.Td fz={fzSmall} w={CW.mobile.parent.names} ta="center">
								{getTruncatedString(parentRow.submissionProjectName || "N/A Project") +
									" by " +
									getTruncatedString(parentRow.projectOwner?.username ?? "N/A")}
							</Table.Td>
						</>
					) : (
						<>
							<Table.Td fz={fzSmall} w={CW.desktop.parent.studentName} ta="center">
								{parentRow.projectOwner?.username || "N/A User"}
							</Table.Td>
							<Table.Td fz={fzSmall} w={CW.desktop.parent.projectName} ta="center">
								{parentRow.submissionProjectName || "N/A Project"}
							</Table.Td>
						</>
					)}
					{/* mp3 Backing Track */}
					<Table.Td
						fz={fzSmall}
						w={
							isMobile ? CW.mobile.parent.uploadVideo : CW.desktop.parent.uploadVideo
						}
						ta="center"
					>
						{
							<Button
								variant="default"
								size="compact-sm"
								fz={fzSmall}
								c={theme.colors.blue[8]}
								onClick={(e) => {
									createFilePicker(parentRow.id);
									e.stopPropagation();
									// don't expand the row if only the mp3 button is clicked
								}}
								bg={
									parentRow.dateFulfilled
										? new Date(parentRow.editorDueDate) < new Date()
											? theme.colors.yellow[1]
											: theme.colors.green[1]
										: new Date(parentRow.editorDueDate) < new Date()
										? theme.colors.red[1]
										: "white"
								}
							>
								{parentRow.dateFulfilled ? (
									new Date(parentRow.editorDueDate) < new Date() ? (
										<span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
											Task Completed <IconCameraCheck size={fzMed} />
										</span>
									) : (
										<span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
											Submit Again <IconCameraRotate size={fzMed} />
										</span>
									)
								) : new Date(parentRow.editorDueDate) < new Date() ? (
									<span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
										Past Due <IconCameraExclamation size={fzMed} />
									</span>
								) : (
									<span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
										Not Yet Submitted <IconCameraSearch size={fzMed} />
									</span>
								)}
							</Button>
						}
					</Table.Td>
					{/* Expand */}
					<Table.Td
						w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
					>
						<Box ml={isMobile ? -7 : 0}>
							<IconMathGreater
								size={fzMed}
								style={{
									transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
								}}
							/>
						</Box>
					</Table.Td>
				</Table.Tr>
				{isExpanded && (
					<Table.Tr>
						<Table.Td colSpan={isMobile ? 4 : 5} p={0}>
							<Box
								style={{
									overflowX: "auto",
									width: "100%",
								}}
							>
								<Table w={"100%"}>
									<Table.Thead>
										<Table.Tr>
											<Table.Th
												w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
												ta="center"
											></Table.Th>
											<Table.Th
												w={
													isMobile
														? CW.mobile.child.instrumentName
														: CW.desktop.child.instrumentName
												}
												ta="center"
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													Instrument
												</Text>
											</Table.Th>
											<Table.Th
												w={
													isMobile
														? CW.mobile.child.taskFulfilledDate
														: CW.desktop.child.taskFulfilledDate
												}
												ta="center"
												// only nowrap right now
												style={{ whiteSpace: "nowrap" }}
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													{isMobile ? "Submitted" : "Clip Submission Date"}
												</Text>
											</Table.Th>
											<Table.Th
												w={
													isMobile
														? CW.mobile.child.downloadClip
														: CW.desktop.child.downloadClip
												}
												ta="center"
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													{isMobile ? "Download" : "Download Clip"}
												</Text>
											</Table.Th>
											<Table.Th
												w={
													isMobile
														? CW.mobile.child.rightIndent
														: CW.desktop.child.rightIndent
												}
												ta="center"
											></Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{parentRow.instrumenatlistTasks.map((childRow) => (
											// keeping task = child row
											<Table.Tr key={childRow.id}>
												{/* Indent */}
												<Table.Td
													fz={fzSmall}
													w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
													ta="center"
												>
													<Text fz={fzSmall * 0.9} c={theme.colors.blue[6]} fw={500}></Text>
												</Table.Td>
												{/* Instrument */}
												<Table.Td
													fz={fzSmall}
													w={
														isMobile
															? CW.mobile.child.instrumentName
															: CW.desktop.child.instrumentName
													}
													ta="center"
												>
													{isMobile
														? getTruncatedString(childRow.instrument.name)
														: childRow.instrument.name}
												</Table.Td>
												{/* Task Fulfilled Date */}
												<Table.Td
													fz={fzSmall}
													w={
														isMobile
															? CW.mobile.child.taskFulfilledDate
															: CW.desktop.child.taskFulfilledDate
													}
													ta="center"
												>
													{childRow.dateFulfilled?.toLocaleString() ?? "Not yet fulfilled"}
												</Table.Td>
												{/* Clip Download */}
												<Table.Td
													fz={fzSmall}
													w={
														isMobile
															? CW.mobile.child.downloadClip
															: CW.desktop.child.downloadClip
													}
													ta="center"
												>
													<Button
														fz={fzSmall}
														disabled={childRow.dateFulfilled == null}
														onClick={() => {
															const url = `${appSettings.S3BucketBaseUrl}InstrumentalistVideos/${childRow.id}.mp4`;
															// regex replace spaces with underscores. make prettier if project name has spaces
															const filename =
																`${parentRow.submissionProjectName}-${childRow.instrument.name}.mp4`.replace(
																	/ /g,
																	"_"
																);
															downloadBlob(url, filename);
															Utils.displayNotification(
																"Downloaded " +
																	(parentRow.submissionProjectName || "Project") +
																	" " +
																	(childRow.instrument.name || "Project"),
																"green",
																"",
																<IconDownload size={fzMed} />
															);
														}}
														variant="default"
														size="compact-sm"
											
														bg={theme.colors.white[1]}
													>
														<IconCameraDown size={fzMed} />
													</Button>
												</Table.Td>
												<Table.Td
													w={
														isMobile
															? CW.mobile.child.rightIndent
															: CW.desktop.child.rightIndent
													}
													ta="center"
												></Table.Td>
											</Table.Tr>
										))}
										{/* feedback duty - check if user
										 has also been assigned feedback */}

										<Table.Tr key={parentRow.id + "fb"}>
											{/* Indent */}
											<Table.Td
												fz={fzSmall}
												w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
												ta="center"
											>
												<Text fz={fzSmall * 0.9} c={theme.colors.blue[6]} fw={500}></Text>
											</Table.Td>
											{/* Instrument */}
											<Table.Td
												fz={fzSmall}
												w={
													isMobile
														? CW.mobile.child.instrumentName
														: CW.desktop.child.instrumentName
												}
												ta="center"
											>
												Feedback Duty
											</Table.Td>
											{/* Feedback Date Fulfilled */}
											<Table.Td
												fz={fzSmall}
												w={
													isMobile
														? CW.mobile.child.taskFulfilledDate
														: CW.desktop.child.taskFulfilledDate
												}
												ta="center"
											>
												{" "}
												{parentRow.feedbackDateFulfilled?.toLocaleString() ??
													"Not yet fulfilled"}
											</Table.Td>
											{/* File Upload */}
											<Table.Td
												fz={fzSmall}
												w={
													isMobile
														? CW.mobile.child.downloadClip
														: CW.desktop.child.downloadClip
												}
												ta="center"
											>
												{/* 'status bar' and submit button combined */}
												<Button
													fz={fzSmall}
									
													disabled={parentRow.feedbackDateFulfilled == null}
													onClick={() => {
														const url = `${appSettings.S3BucketBaseUrl}SubmissionFeedbackVideos/${parentRow.id}.mp4`;
														// regex replace spaces with underscores. make prettier if project name has spaces
														const filename =
															`${parentRow.submissionProjectName}-Feedback.mp4`.replace(
																/ /g,
																"_"
															);
														downloadBlob(url, filename);
														Utils.displayNotification(
															"Downloaded " +
																(parentRow.submissionProjectName || "Project") +
																" Feedback",
															"green",
															"",
															<IconDownload size={fzMed} />
														);
													}}
													variant="default"
													size="compact-sm"
								
													bg={theme.colors.white[1]}
												>
													<IconCameraDown size={fzMed} />
												</Button>
											</Table.Td>
										</Table.Tr>
									</Table.Tbody>
								</Table>
							</Box>
						</Table.Td>
					</Table.Tr>
				)}
			</React.Fragment>
		);
	});

	return (
		<Group>
			<Text fz={fzSmall} p={"auto"} c={theme.colors.blue[0]}>
				Download clips and submit videos here.
			</Text>
			<Box
				w={"100%"}
				h={TASKS_HEIGHT}
				bg={theme.colors.blue[0]}
				style={{
					borderRadius: 16,
					boxSizing: "border-box",
					overflowX: "hidden",
				}}
			>
				<Table
					horizontalSpacing="md"
					verticalSpacing="xs"
					miw="auto"
					layout="fixed"
					 style={{ tableLayout: "fixed" }} 
				>
					<Table.Thead>
						<Table.Tr>
							<Table.Th
								// width of "due date" column on desktop
								w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW} ta="center">
									Due Date
								</Text>
							</Table.Th>
							{isMobile ? (
								<Table.Th w={CW.desktop.parent.studentName} ta="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										Info
									</Text>
								</Table.Th>
							) : (
								<>
									<Table.Th w={CW.desktop.parent.projectName} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											User Name
										</Text>
									</Table.Th>
									<Table.Th w={CW.desktop.parent.projectName} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											Project Name
										</Text>
									</Table.Th>
								</>
							)}
							<Table.Th
								// width of backing track
								w={
									isMobile ? CW.mobile.parent.uploadVideo : CW.desktop.parent.uploadVideo
								}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW}>
									{isMobile ? "Submit" : "Submit Edited Project"}
								</Text>
							</Table.Th>
							<Table.Th
								w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
								ta="center"
							>
								<Text fz={fzSmall} fw={500}></Text>
							</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{isLoading ? (
							skeletonRows
						) : rows && rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={6}>
									<Text fw={500} ta="center">
										No Tasks
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</Box>
		</Group>
	);
};

export default EditorTasks;
